import React, { useState } from 'react';
import { Alert, App, Button, Segmented, Table, Tag, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { fmtDate, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;

/** Task owners (IT, Admin, Finance, line managers) see and close their onboarding work here. */
const MyOnboardingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [includeClosed, setIncludeClosed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.myOnboardingTasks(includeClosed),
    queryFn: () => hrmsService.myOnboardingTasks(includeClosed),
  });

  const complete = async (t: any) => {
    setBusy(t.id);
    try {
      await hrmsService.completeOnboardingTask(t.plan_id, t.id);
      message.success(`${t.task_name} done`);
      await queryClient.invalidateQueries({ queryKey: ['hrms', 'my-onboarding-tasks'] });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboardingPlan(t.plan_id) });
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const rows = data?.rows || [];
  const overdue = rows.filter((t: any) => t.status === 'Pending' && t.due_date && dayjs(t.due_date).isBefore(dayjs(), 'day')).length;

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="My onboarding tasks"
          subtitle="Work assigned to you for new joiners"
          primaryActions={
            <Segmented
              value={includeClosed ? 'all' : 'open'}
              onChange={(v) => setIncludeClosed(v === 'all')}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'all', label: 'Include closed' },
              ]}
            />
          }
        />
      }
    >
      {data && !data.employee_id && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Your user account is not linked to an employee record, so no onboarding tasks can be assigned to you." />
      )}
      {overdue > 0 && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={`${overdue} task(s) overdue`} />}
      <Table
        rowKey="id"
        loading={isFetching}
        dataSource={rows}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 900 }}
        columns={[
          {
            title: 'Due',
            dataIndex: 'due_date',
            width: 130,
            render: (v, t: any) => {
              const late = t.status === 'Pending' && v && dayjs(v).isBefore(dayjs(), 'day');
              return <Text type={late ? 'danger' : undefined}>{fmtDate(v)}</Text>;
            },
          },
          { title: 'Task', dataIndex: 'task_name' },
          {
            title: 'New joiner',
            dataIndex: 'employee_name',
            render: (v, t: any) => (
              <a onClick={() => navigate(`/hr/onboarding/${t.plan_id}`)}>
                {v} <Text type="secondary">({t.plan_number})</Text>
              </a>
            ),
          },
          { title: 'Department', dataIndex: 'department_name', width: 160 },
          { title: 'Joins', dataIndex: 'join_date', width: 120, render: (v) => fmtDate(v) },
          {
            title: '',
            key: 'a',
            width: 120,
            render: (_, t: any) =>
              t.status === 'Pending' ? (
                <Button size="small" type="primary" icon={<CheckOutlined />} loading={busy === t.id} onClick={() => complete(t)}>
                  Done
                </Button>
              ) : (
                <Tag color={t.status === 'Done' ? 'green' : 'default'}>{t.status === 'Skipped' ? 'Not applicable' : t.status}</Tag>
              ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default MyOnboardingTasksPage;
